import clsx from 'clsx'
import {
  type CSSProperties,
  type FC,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { ResponsiveImageProps } from '@/types/components/responsive-image'
import type { SanityImage } from '@/types/sanity'
import {
  generateImageSrcSetByResponsiveImage,
  getImageSizes,
  getTransformationProxyUrl,
  type ResponsiveImageSizeProp,
} from '@/utilities/image'
import './responsive-image.scss'

export const ResponsiveImage: FC<ResponsiveImageProps> = ({
  document,
  overrides,
  children,
}) => {
  const { desktopImage, mobileImage, _id: imageDocumentId, altText } = document

  const [isLoaded, setIsLoaded] = useState(false)
  const [isErrored, setIsErrored] = useState(false)

  const imgRef = useRef<HTMLImageElement | null>(null)

  const { imageRatio: desktopImageRatio } = desktopImage

  const overrideDesktopImageRatio = overrides?.desktopAspectRatio
  const overrideMobileImageRatio = overrides?.mobileAspectRatio
  const imgLoadingAttribute = overrides?.loading || 'lazy'

  const imgObjectFit = overrides?.objectFit || 'cover'

  const pictureStyle: CSSProperties & { [key: string]: string } = {
    '--mobile-ratio':
      overrideMobileImageRatio || mobileImage.imageRatio || desktopImageRatio,
    '--desktop-ratio': overrideDesktopImageRatio || desktopImageRatio,
  }

  const imageStyle: CSSProperties = {}

  if (imgObjectFit !== 'cover') {
    imageStyle.objectFit = imgObjectFit
  }

  const customSizes = overrides?.customSizes?.length
    ? overrides.customSizes.sort((a, b) => a - b)
    : []
  const mobileImageSize: ResponsiveImageSizeProp = customSizes.length
    ? `custom:${customSizes[0]}`
    : 'md'

  const fallbackImageUrl = getTransformationProxyUrl({
    source: desktopImage,
    variant: 'desktop',
    size: 'md',
    targetFormat: 'webp',
    documentId: imageDocumentId,
  })

  const handleLoad = () => {
    setIsLoaded(true)
  }

  const handleError = () => {
    setIsErrored(true)
  }

  useEffect(() => {
    if (imgRef.current?.complete) {
      handleLoad()
    }
  }, [])

  return (
    <picture
      className={clsx([
        'responsive-image',
        isLoaded && 'responsive-image--loaded',
      ])}
      style={pictureStyle}
    >
      {mobileImage.asset && (
        <Fragment>
          <source
            srcSet={getTransformationProxyUrl({
              source: mobileImage as SanityImage,
              variant: 'mobile',
              size: mobileImageSize,
              targetFormat: 'avif',
              documentId: imageDocumentId,
            })}
            media="(max-width: 767px)"
            type="image/avif"
          />
          <source
            srcSet={getTransformationProxyUrl({
              source: mobileImage as SanityImage,
              variant: 'mobile',
              size: mobileImageSize,
              targetFormat: 'webp',
              documentId: imageDocumentId,
            })}
            media="(max-width: 767px)"
            type="image/webp"
          />
        </Fragment>
      )}
      <source
        srcSet={generateImageSrcSetByResponsiveImage(
          document,
          'avif',
          customSizes,
        )}
        sizes={getImageSizes(customSizes)}
        type="image/avif"
      />
      <source
        srcSet={generateImageSrcSetByResponsiveImage(
          document,
          'webp',
          customSizes,
        )}
        sizes={getImageSizes(customSizes)}
        type="image/webp"
      />
      <img
        className={clsx([
          'responsive-image__img',
          isErrored && 'responsive-image__img--errored',
        ])}
        src={fallbackImageUrl}
        alt={altText || imageDocumentId}
        style={imageStyle}
        loading={imgLoadingAttribute}
        // TODO: Replace with proper implementation
        // @ts-expect-error
        fetchpriority={imgLoadingAttribute === 'eager' ? 'high' : 'auto'}
        ref={imgRef}
        onLoad={handleLoad}
        onError={handleError}
      />
      {children}
      {mobileImage?.asset?.metadata ? (
        <Fragment>
          <div
            className="responsive-image__lqip-mobile"
            style={{
              backgroundImage: `url(${mobileImage.asset.metadata.lqip})`,
            }}
          />
          <div
            className="responsive-image__lqip-desktop"
            style={{
              backgroundImage: `url(${desktopImage.asset.metadata.lqip})`,
            }}
          />
        </Fragment>
      ) : (
        desktopImage?.asset?.metadata && (
          <div
            className="responsive-image__lqip"
            style={{
              backgroundImage: `url(${desktopImage.asset.metadata.lqip})`,
            }}
          />
        )
      )}
    </picture>
  )
}
